import { useState, useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Spin } from 'antd'
import { AdminLayout } from './components/admin/AdminLayout'
import { PoolActivityProvider } from './context/PoolActivityContext'
import { DashboardPage } from './pages/DashboardPage'
import { LoginPage } from './pages/LoginPage'

const ChatTemplatesPage = lazy(() => import('./pages/ChatTemplatesPage').then(m => ({ default: m.ChatTemplatesPage })))
const AnalyzeTemplatesPage = lazy(() => import('./pages/AnalyzeTemplatesPage').then(m => ({ default: m.AnalyzeTemplatesPage })))
const AnalyzeTasksPage = lazy(() => import('./pages/AnalyzeTasksPage').then(m => ({ default: m.AnalyzeTasksPage })))
const SystemConfigsPage = lazy(() => import('./pages/SystemConfigsPage').then(m => ({ default: m.SystemConfigsPage })))
const FsDocsPage = lazy(() => import('./pages/FsDocsPage').then(m => ({ default: m.FsDocsPage })))
const CategoryTreePage = lazy(() => import('./pages/CategoryTreePage').then(m => ({ default: m.CategoryTreePage })))
const ActivitiesPage = lazy(() => import('./pages/ActivitiesPage').then(m => ({ default: m.ActivitiesPage })))
const ActivityDetailPage = lazy(() => import('./pages/ActivityDetailPage').then(m => ({ default: m.ActivityDetailPage })))
const OrganizersPage = lazy(() => import('./pages/OrganizersPage').then(m => ({ default: m.OrganizersPage })))
const ToolsPage = lazy(() => import('./pages/ToolsPage').then(m => ({ default: m.ToolsPage })))
const ConnectorsPage = lazy(() => import('./pages/ConnectorsPage').then(m => ({ default: m.ConnectorsPage })))
const ModelsPage = lazy(() => import('./pages/ModelsPage').then(m => ({ default: m.ModelsPage })))
const ProvidersPage = lazy(() => import('./pages/ProvidersPage').then(m => ({ default: m.ProvidersPage })))
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage').then(m => ({ default: m.ApiKeysPage })))
const TTSVoicesPage = lazy(() => import('./pages/TTSVoicesPage').then(m => ({ default: m.TTSVoicesPage })))
const FlowControllersPage = lazy(() => import('./pages/FlowControllersPage').then(m => ({ default: m.FlowControllersPage })))
const ControllerSessionsPage = lazy(() => import('./pages/ControllerSessionsPage').then(m => ({ default: m.ControllerSessionsPage })))
const QueueMonitorPage = lazy(() => import('./pages/QueueMonitorPage').then(m => ({ default: m.QueueMonitorPage })))
const PromptTestPage = lazy(() => import('./pages/PromptTestPage').then(m => ({ default: m.PromptTestPage })))
const AgentHarnessPage = lazy(() => import('./pages/AgentHarnessPage').then(m => ({ default: m.AgentHarnessPage })))
const EduVerifyReviewPage = lazy(() => import('./pages/EduVerifyReviewPage').then(m => ({ default: m.EduVerifyReviewPage })))
const PoolPlansPage = lazy(() => import('./pages/PoolPlansPage').then(m => ({ default: m.PoolPlansPage })))
const PoolMembersPage = lazy(() => import('./pages/PoolMembersPage').then(m => ({ default: m.PoolMembersPage })))
const PoolEncountersPage = lazy(() => import('./pages/PoolEncountersPage').then(m => ({ default: m.PoolEncountersPage })))
const PoolRecommendationsPage = lazy(() => import('./pages/PoolRecommendationsPage').then(m => ({ default: m.PoolRecommendationsPage })))
const PoolMatchLogsPage = lazy(() => import('./pages/PoolMatchLogsPage').then(m => ({ default: m.PoolMatchLogsPage })))
const PoolPushLogsPage = lazy(() => import('./pages/PoolPushLogsPage').then(m => ({ default: m.PoolPushLogsPage })))
const PoolUserMatchStatsPage = lazy(() => import('./pages/PoolUserMatchStatsPage').then(m => ({ default: m.PoolUserMatchStatsPage })))
const PoolTriggerMatchPage = lazy(() => import('./pages/PoolTriggerMatchPage').then(m => ({ default: m.PoolTriggerMatchPage })))
const PoolTransactionsPage = lazy(() => import('./pages/PoolTransactionsPage').then(m => ({ default: m.PoolTransactionsPage })))
const PoolVenuesPage = lazy(() => import('./pages/PoolVenuesPage').then(m => ({ default: m.PoolVenuesPage })))
const UserProfilesPage = lazy(() => import('./pages/UserProfilesPage').then(m => ({ default: m.UserProfilesPage })))
const UserSearchPage = lazy(() => import('./pages/UserSearchPage').then(m => ({ default: m.UserSearchPage })))
const SeedUsersPage = lazy(() => import('./pages/SeedUsersPage').then(m => ({ default: m.SeedUsersPage })))
const PoolCronStatusPage = lazy(() => import('./pages/PoolCronStatusPage').then(m => ({ default: m.PoolCronStatusPage })))
const PoolPairsPage = lazy(() => import('./pages/PoolPairsPage').then(m => ({ default: m.PoolPairsPage })))
const PoolTodoPage = lazy(() => import('./pages/PoolTodoPage').then(m => ({ default: m.PoolTodoPage })))

const PageFallback = (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
    <Spin size="large" />
  </div>
)

interface AuthUser {
  user_id: string
  username: string
  email: string
  phone: string
  nickname: string
  role: string
}

function getUser(): AuthUser | null {
  try {
    const u = localStorage.getItem('auth_user')
    return u ? JSON.parse(u) : null
  } catch {
    return null
  }
}

export default function App() {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      setUser(getUser())
    }
    setChecking(false)
  }, [])

  const handleLogin = () => {
    setUser(getUser())
  }

  const handleLogout = () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')
    setUser(null)
  }

  if (checking) return null

  if (!user) {
    return <LoginPage onLogin={handleLogin} />
  }

  return (
    <BrowserRouter>
      <PoolActivityProvider>
        <Routes>
          <Route path="/" element={<AdminLayout user={user} onLogout={handleLogout} />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route
              path="*"
              element={
                <Suspense fallback={PageFallback}>
                  <Routes>
                    <Route path="chat-templates" element={<ChatTemplatesPage />} />
                    <Route path="analyze-templates" element={<AnalyzeTemplatesPage />} />
                    <Route path="analyze-tasks" element={<AnalyzeTasksPage />} />
                    <Route path="system-configs" element={<SystemConfigsPage />} />
                    <Route path="fs-docs" element={<FsDocsPage />} />
                    <Route path="category-tree" element={<CategoryTreePage />} />
                    <Route path="organizers" element={<OrganizersPage />} />
                    <Route path="activities" element={<ActivitiesPage />} />
                    <Route path="activities/:id" element={<ActivityDetailPage />} />
                    <Route path="edu-verify-review" element={<EduVerifyReviewPage />} />
                    <Route path="ai-tools" element={<ToolsPage />} />
                    <Route path="ai-connectors" element={<ConnectorsPage />} />
                    <Route path="ai-models" element={<ModelsPage />} />
                    <Route path="ai-providers" element={<ProvidersPage />} />
                    <Route path="ai-apikeys" element={<ApiKeysPage />} />
                    <Route path="tts-voices" element={<TTSVoicesPage />} />
                    <Route path="flow-controllers" element={<FlowControllersPage />} />
                    <Route path="controller-sessions" element={<ControllerSessionsPage />} />
                    <Route path="queue-monitor" element={<QueueMonitorPage />} />
                    <Route path="prompt-test" element={<PromptTestPage />} />
                    <Route path="agent-harness" element={<AgentHarnessPage />} />

                    <Route path="pool/plans" element={<PoolPlansPage />} />
                    <Route path="pool/profiles" element={<UserProfilesPage />} />
                    <Route path="pool/search" element={<UserSearchPage />} />
                    <Route path="pool/members" element={<PoolMembersPage />} />
                    <Route path="pool/encounters" element={<PoolEncountersPage />} />
                    <Route path="pool/recommendations" element={<PoolRecommendationsPage />} />
                    <Route path="pool/pairs" element={<PoolPairsPage />} />
                    <Route path="pool/todo" element={<PoolTodoPage />} />
                    <Route path="pool/user-match-stats" element={<PoolUserMatchStatsPage />} />
                    <Route path="pool/trigger-match" element={<PoolTriggerMatchPage />} />
                    <Route path="pool/match-logs" element={<PoolMatchLogsPage />} />
                    <Route path="pool/push-logs" element={<PoolPushLogsPage />} />
                    <Route path="pool/transactions" element={<PoolTransactionsPage />} />
                    <Route path="pool/venues" element={<PoolVenuesPage />} />
                    <Route path="pool/seed-users" element={<SeedUsersPage />} />
                    <Route path="pool/cron-status" element={<PoolCronStatusPage />} />
                  </Routes>
                </Suspense>
              }
            />
          </Route>
        </Routes>
      </PoolActivityProvider>
    </BrowserRouter>
  )
}
